import { useEffect, useMemo, useState } from 'react';
import { Card, Button, Input, Badge } from '../components/Common';
import { usePhysios } from '../hooks/usePhysios';
import { useCreatePoll, usePolls } from '../hooks/usePolls';
import type { PollCreateResponse, PollSummary } from '../types';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function generateStatusText(poll: PollSummary): string {
  const inNames = poll.physios.filter(p => p.response === 'in').map(p => p.name);
  const outNames = poll.physios.filter(p => p.response === 'out').map(p => p.name);
  const pendingNames = poll.physios.filter(p => !p.response).map(p => p.name);

  const lines: string[] = [];

  if (poll.message) {
    lines.push(poll.message);
  } else {
    lines.push(`Coverage update - ${formatShortDate(poll.created_at)}`);
  }

  lines.push('');

  if (inNames.length > 0) {
    lines.push(`In: ${inNames.join(', ')}`);
  }
  if (outNames.length > 0) {
    lines.push(`Out: ${outNames.join(', ')}`);
  }
  if (pendingNames.length > 0) {
    lines.push(`Waiting: ${pendingNames.join(', ')}`);
  }

  return lines.join('\n');
}

const POLLS_PER_PAGE = 10;

export function Polls() {
  const { data: physios, isLoading: physiosLoading } = usePhysios();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: pollsData, isLoading: pollsLoading } = usePolls(currentPage, POLLS_PER_PAGE);
  const createPoll = useCreatePoll();

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdPoll, setCreatedPoll] = useState<PollCreateResponse | null>(null);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);

  const polls = pollsData?.polls;
  const pagination = pollsData?.pagination;

  const allPhysioIds = useMemo(() => (physios || []).map((p) => p.id), [physios]);

  useEffect(() => {
    if (!hasInitializedSelection && allPhysioIds.length > 0) {
      setSelectedIds(allPhysioIds);
      setHasInitializedSelection(true);
    }
  }, [allPhysioIds, hasInitializedSelection]);

  const togglePhysio = (id: number) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((pid) => pid !== id) : [...current, id]
    );
  };

  const selectAll = () => {
    setSelectedIds(allPhysioIds);
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  const handleCreatePoll = async () => {
    if (selectedIds.length === 0) {
      setCreateError('Select at least one physio.');
      return;
    }
    setCreateError(null);
    try {
      const result = await createPoll.mutateAsync({
        physio_ids: selectedIds,
        message: message.trim() || undefined,
      });
      setCreatedPoll(result.data);
      // Reset to first page to see the new poll
      setCurrentPage(1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create poll';
      setCreateError(message);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy';
      setCreateError(message);
    }
  };

  const perPhysioCopyText = createdPoll
    ? createdPoll.links.per_physio
        .map((link) => `${link.name}: ${link.link}`)
        .join('\n')
    : '';

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Polls</h1>
      </div>

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create New Poll</h2>
            <p className="text-sm text-gray-500">
              Select physios, then share the link via WhatsApp or any mobile app.
            </p>
          </div>

          <Input
            label="Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g., Can you cover Thu 2pm? Reply I'm in if you can."
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={selectAll} disabled={!physios?.length}>
              Select All
            </Button>
            <Button variant="secondary" size="sm" onClick={deselectAll} disabled={!physios?.length}>
              Deselect All
            </Button>
            <span className="text-sm text-gray-500">
              {selectedIds.length} of {physios?.length || 0} selected
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {physiosLoading && (
              <p className="text-sm text-gray-500">Loading physios...</p>
            )}
            {!physiosLoading && physios?.length === 0 && (
              <p className="text-sm text-gray-500">No physios available.</p>
            )}
            {physios?.map((physio) => (
              <label
                key={physio.id}
                className={`flex items-center gap-2 p-2 rounded border ${
                  selectedIds.includes(physio.id) ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(physio.id)}
                  onChange={() => togglePhysio(physio.id)}
                />
                <span className="text-sm text-gray-800">{physio.name}</span>
              </label>
            ))}
          </div>

          {createError && (
            <p className="text-sm text-red-600" role="alert">
              {createError}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={handleCreatePoll} isLoading={createPoll.isPending}>
              Execute Poll
            </Button>
          </div>

          {createdPoll && (
            <div className="mt-4 rounded border border-green-200 bg-green-50 p-3 space-y-2">
              <p className="text-sm text-green-800 font-medium">Poll created.</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="text-sm text-gray-700">Shared link:</span>
                <code className="text-xs bg-white border px-2 py-1 rounded break-all">
                  {createdPoll.links.shared}
                </code>
                <Button size="sm" variant="secondary" onClick={() => handleCopy(createdPoll.links.shared)}>
                  Copy
                </Button>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="text-sm text-gray-700">Per-physio links:</span>
                <Button size="sm" variant="secondary" onClick={() => handleCopy(perPhysioCopyText)}>
                  Copy All
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Poll History</h2>
              <p className="text-sm text-gray-500">
                {pagination ? `${pagination.total} polls total` : 'Track who is in, out, or pending.'}
              </p>
            </div>
          </div>

          {pollsLoading && <p className="text-sm text-gray-500">Loading polls...</p>}
          {!pollsLoading && (!polls || polls.length === 0) && (
            <p className="text-sm text-gray-500">No polls created yet.</p>
          )}

          {polls?.map((poll) => (
            <div key={poll.id} className="border rounded-lg p-3 bg-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Poll #{poll.id} • {formatDate(poll.created_at)}
                  </p>
                  {poll.message && <p className="text-sm text-gray-600 mt-1">{poll.message}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="success">In: {poll.in_count}</Badge>
                  <Badge variant="danger">Out: {poll.out_count}</Badge>
                  <Badge variant="info">Pending: {poll.pending_count}</Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleCopy(generateStatusText(poll))}
                  >
                    Copy Status
                  </Button>
                </div>
              </div>

              {poll.physios.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {poll.physios.map((physio) => (
                    <div key={physio.physio_id} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                      <span className="text-gray-700">{physio.name}</span>
                      {physio.response === 'in' && <Badge variant="success">In</Badge>}
                      {physio.response === 'out' && <Badge variant="danger">Out</Badge>}
                      {!physio.response && <Badge variant="info">Pending</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <p className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
